import { faUpload } from "@fortawesome/free-solid-svg-icons"
import { computed, reactive, shallowRef, type HTMLAttributes, type InputHTMLAttributes } from "vue"
import { defineComponent, inferProps, propsWithDefaults, useCustomCheck, type ReactiveComponent } from "vue-utils"
import BootstrapButton from "../form/BootstrapButton"
import Icon from "../Icon"
import FileIndicator from "./FileIndicator"
import "./FileUpload.scss"
import { useUploadValidation } from "./UploadValidation"

interface Props {
	files: File[]
	setFiles(files: File[]): void

	name?: string
	accept?: string[]
	capture?: InputHTMLAttributes["capture"]
	multiple?: InputHTMLAttributes["multiple"]
	maxFileSizeBytes?: number
	disabled?: boolean
	required?: boolean
	inputAttrs?: InputHTMLAttributes
}

const FileUpload: ReactiveComponent<Props, HTMLAttributes> = (initialProps, { attrs }) => {
	const props = propsWithDefaults(initialProps, {
		multiple: false,
	})

	const { validateUpload } = useUploadValidation()
	useCustomCheck(() =>
		validateUpload(props.files, {
			required: props.required,
			maxUploadSize: props.maxFileSizeBytes,
			allowedMimeTypes: props.accept,
		})
	)

	const containerRef = shallowRef<HTMLInputElement>()
	const fileInputRef = shallowRef<HTMLInputElement>()
	const draggingFiles = shallowRef<number>(0)

	const draggingMimeTypes = reactive(new Set<string>())

	const errorMessage = computed(() => {
		const dragging = draggingFiles.value
		if (dragging > 1 && !props.multiple) {
			return "May only upload 1 file"
		}
		if (props.accept && Array.from(draggingMimeTypes).some((type) => !props.accept!.includes(type))) {
			return dragging > 1
				? "One of more of the files you are trying to upload has a file type that is not allowed. Please select a different file"
				: "This file type is not allowed. Please select a different file"
		}
		return null
	})

	function triggerUploadPicker() {
		fileInputRef.value?.click()
	}

	function changeFiles(files: FileList | null) {
		let validFiles: File[] = []
		if (files) validFiles = Array.from(files)

		if (props.accept) {
			validFiles.forEach((file) => {
				if (props.accept!.indexOf(file.type) === -1) validFiles.splice(validFiles.indexOf(file), 1)
			})
		}

		if (!props.multiple) {
			if (validFiles.length === 1) {
				props.setFiles([validFiles[0]])
			}
		} else if (validFiles) {
			const result: File[] = []
			const exists = (file: File) =>
				result.some((f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified)

			result.push(...props.files.filter((f) => !exists(f)))
			result.push(...validFiles.filter((f) => !exists(f)))

			props.setFiles(result)
		}
		if (fileInputRef.value) {
			fileInputRef.value.files = files
		}
	}

	function handleDragEnd(e: Event) {
		if (e.target === containerRef.value) {
			draggingFiles.value = 0
			draggingMimeTypes.clear()
		}
	}

	function handleDrag(event: DragEvent) {
		event.stopPropagation()
		event.preventDefault()
		if (event.dataTransfer) {
			const draggedItems = Array.from(event.dataTransfer.items)
			const fileItems = draggedItems.filter((item) => item.kind === "file").length
			for (const item of draggedItems) {
				draggingMimeTypes.add(item.type)
			}
			draggingFiles.value = fileItems
		} else {
			handleDragEnd(event)
		}
	}

	function handleFileDrop(event: DragEvent) {
		event.preventDefault()
		handleDrag(event)

		if (errorMessage.value === null && (event.dataTransfer || event.dataTransfer!.items.length > 0)) {
			changeFiles(event.dataTransfer!.files)
		}
		handleDragEnd(event)
	}

	const renderSelect = () => (
		<>
			<Icon icon={faUpload} />
			<h3>Drag & drop a file here</h3>
			<span>
				or{" "}
				<button type="button" onClick={triggerUploadPicker}>
					pick a file to upload
				</button>
			</span>
		</>
	)
	const renderFiles = () => (
		<>
			<div style={{ overflow: "auto" }}>
				<ul>
					{props.files.map((file, i) => (
						<li key={file.name}>
							<FileIndicator file={file} remove={() => props.setFiles(props.files.filter((_, index) => index !== i))} />
						</li>
					))}
				</ul>
			</div>
			<span class="flex justify-end" style={{ borderTop: "1px solid rgba(0, 0, 0, 0.25)", paddingTop: "0.5rem" }}>
				<BootstrapButton color="success" onClick={triggerUploadPicker}>
					<Icon icon={faUpload} />
					{props.multiple ? "Add another file" : "Pick a different file"}
				</BootstrapButton>
			</span>
		</>
	)

	const renderDragging = () => (
		<>
			<Icon icon={faUpload} />
			<h3 style={{ marginBottom: "2.35rem" }}>
				{draggingFiles.value > 1 ? "Drop files to upload" : "Drop file to upload"}
			</h3>
		</>
	)

	const renderContent = () => {
		if (errorMessage.value) {
			return <h5 style={{ fontStyle: "bold" }}>{errorMessage.value}</h5>
		}
		if (draggingFiles.value > 0) {
			return renderDragging()
		}
		if (props.files.length > 0) {
			return renderFiles()
		}
		return renderSelect()
	}

	return () => (
		<div
			ref={containerRef}
			class="file-upload-picker"
			onDrop={handleFileDrop}
			onDragenter={handleDrag}
			onDragover={handleDrag}
			onDragleave={handleDragEnd}
			data-drag={(draggingFiles.value > 0).toString()}
			data-invalid={Boolean(errorMessage.value).toString()}
			data-with-file={(props.files.length > 0).toString()}
			{...attrs}
		>
			<div style={{ pointerEvents: draggingFiles.value > 0 ? "none" : "all" }}>{renderContent()}</div>
			<input
				ref={fileInputRef}
				name={props.name}
				type="file"
				style={{ display: "none" }}
				accept={props.accept ? props.accept.join(",") : undefined}
				capture={props.capture}
				multiple={props.multiple}
				onInput={(e) => changeFiles((e.target as HTMLInputElement).files)}
				{...props.inputAttrs}
			/>
		</div>
	)
}

export default defineComponent(FileUpload, inferProps<Props>())
